import { safelyParseJson } from '@olivierpascal/helpers';
import * as Cookies from 'es-cookie';

import type { IMakeRequired } from '~/helpers/types';
import type { IStorage, IStorageAttributes, IStorageOptions } from './types';
import { defaultOptions } from './types';

export class CookieStorage implements IStorage {
  private static defaultInstance: CookieStorage;
  private options: IMakeRequired<IStorageOptions, 'prefix'>;
  private readonly namespacedPrefix: string;

  public constructor(options?: IStorageOptions) {
    this.options = { ...defaultOptions, ...options };
    this.namespacedPrefix = this.options.namespace
      ? `${this.options.prefix}${this.options.namespace}`
      : this.options.prefix;
  }

  public static getDefaultInstance(): CookieStorage {
    if (!CookieStorage.defaultInstance) {
      CookieStorage.defaultInstance = new CookieStorage();
    }

    return CookieStorage.defaultInstance;
  }

  private uriFromKey(key: string): string {
    return `${this.namespacedPrefix}::${key}`;
  }

  public set<T>(key: string, value: T, options?: IStorageAttributes): void {
    const uri = this.uriFromKey(key);
    let cookieAttributes: Cookies.CookieAttributes = {};

    if ('https:' === location.protocol) {
      cookieAttributes = {
        secure: true,
        sameSite: 'none',
      };
    }

    if (options?.daysUntilExpire) {
      cookieAttributes.expires = options.daysUntilExpire;
    }

    if (options?.cookieDomain) {
      cookieAttributes.domain = options.cookieDomain;
    }

    Cookies.set(uri, JSON.stringify(value), cookieAttributes);
  }

  public get<T>(key: string): T | undefined {
    const uri = this.uriFromKey(key);
    const json = Cookies.get(uri);

    if (typeof json === 'undefined') {
      return;
    }

    const payload = safelyParseJson<T>(json);

    return payload;
  }

  public delete(key: string, options?: IStorageAttributes): void {
    const uri = this.uriFromKey(key);
    const cookieAttributes: Cookies.CookieAttributes = {};

    if (options?.cookieDomain) {
      cookieAttributes.domain = options.cookieDomain;
    }

    Cookies.remove(uri, cookieAttributes);
  }
}
